import { useMemo, useState } from 'react'

import type { Periodo } from '@/lib/supabase/tipos-bd'
import { hoy } from '@/modules/tablero/semana'
import { Aviso } from '@/shared/ui/Aviso/Aviso'
import { Cargando } from '@/shared/ui/Estados/Cargando'

import { FiltrosAuditoria, type Filtros } from './FiltrosAuditoria'
import { LineaDeTiempo } from './LineaDeTiempo'
import { PorPersona } from './PorPersona'
import { SelectorDePeriodo } from './SelectorDePeriodo'
import { bordes, useAuditoria } from './useAuditoria'
import estilos from './PaginaAuditoria.module.css'

/**
 * AUDITORÍA (0249).
 *
 * Johan: «nos falta la sección de auditoría… donde podamos ver quién hace
 * todo dentro de la app: quién programa, quién acepta cotización, quién
 * cierra, etc., y que eso no sea editable».
 *
 * Arriba se dice primero lo que hace que esto valga algo: el registro es
 * inmutable —un disparador en la base rechaza cualquier edición o borrado,
 * venga de la app, de una función o de la base misma—. Después el periodo,
 * los filtros y un resumen; y dos formas de leerlo: la línea de tiempo (qué
 * pasó, día por día) o por persona (qué hizo cada uno).
 *
 * Aquí no hay un solo botón que escriba. Es a propósito.
 */
type Vista = 'tiempo' | 'personas'

const SIN_FILTROS: Filtros = { persona: '', accion: '', documento: '', buscar: '' }

export function PaginaAuditoria() {
  const [periodo, setPeriodo] = useState<Periodo>('semana')
  const [fecha, setFecha] = useState(hoy())
  const [filtros, setFiltros] = useState<Filtros>(SIN_FILTROS)
  const [vista, setVista] = useState<Vista>('tiempo')
  const { desde, hasta } = bordes(periodo, fecha)
  const { data: registros = [], isLoading, error } = useAuditoria(desde, hasta)

  const personas = useMemo(
    () => [...new Set(registros.map((r) => r.actor_nombre))].sort((a, b) => a.localeCompare(b)),
    [registros],
  )

  const visibles = useMemo(() => {
    const t = filtros.buscar.trim().toLowerCase()
    return registros.filter(
      (r) =>
        (!filtros.persona || r.actor_nombre === filtros.persona) &&
        (!filtros.accion || r.tipo === filtros.accion) &&
        (!filtros.documento || r.documento_tipo === filtros.documento) &&
        (!t || `${r.documento_numero ?? ''} ${r.notas ?? ''}`.toLowerCase().includes(t)),
    )
  }, [registros, filtros])

  const resumen = {
    acciones: visibles.length,
    personas: new Set(visibles.filter((r) => r.actor_rol).map((r) => r.actor_nombre)).size,
    aprobaciones: visibles.filter((r) => r.tipo === 'aprobacion').length,
    rechazos: visibles.filter((r) => r.tipo === 'rechazo').length,
  }

  return (
    <div className={estilos.pagina}>
      {/* ── Lo primero: que no se puede tocar ──────────────────────────── */}
      <section className={estilos.sello} aria-label="Registro inmutable">
        <span className={estilos.selloIcono} aria-hidden="true">
          <svg
            viewBox="0 0 24 24"
            width="22"
            height="22"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <rect x="5" y="10.5" width="14" height="10" rx="2.2" />
            <path d="M8 10.5V7.5a4 4 0 0 1 8 0v3" />
            <circle cx="12" cy="15.5" r="1.4" />
          </svg>
        </span>
        <div>
          <p className={estilos.selloTitulo}>Registro inmutable</p>
          <p className={estilos.selloTexto}>
            Nadie puede editarlo ni borrarlo, ni desde la app ni desde la base. Cada acción queda
            con el nombre y el rol que tenía la persona en ese momento.
          </p>
        </div>
      </section>

      {/* ── Periodo, filtros y vista ───────────────────────────────────── */}
      <section className={estilos.controles}>
        <div className={estilos.controlesTope}>
          <SelectorDePeriodo
            periodo={periodo}
            desde={desde}
            hasta={hasta}
            onPeriodo={(p) => {
              setPeriodo(p)
              setFecha(hoy())
            }}
            onFecha={setFecha}
          />
          <div className={estilos.segmentos} role="group" aria-label="Cómo verlo">
            <button
              type="button"
              className={vista === 'tiempo' ? estilos.segmentoVivo : undefined}
              aria-pressed={vista === 'tiempo'}
              onClick={() => setVista('tiempo')}
            >
              Línea de tiempo
            </button>
            <button
              type="button"
              className={vista === 'personas' ? estilos.segmentoVivo : undefined}
              aria-pressed={vista === 'personas'}
              onClick={() => setVista('personas')}
            >
              Por persona
            </button>
          </div>
        </div>
        <FiltrosAuditoria filtros={filtros} personas={personas} onCambio={setFiltros} />
      </section>

      {/* ── El resumen ─────────────────────────────────────────────────── */}
      <div className={estilos.resumen}>
        <p>
          <b>{resumen.acciones}</b> {resumen.acciones === 1 ? 'acción' : 'acciones'}
        </p>
        <p>
          <b>{resumen.personas}</b> {resumen.personas === 1 ? 'persona activa' : 'personas activas'}
        </p>
        <p className={estilos.resumenBien}>
          <b>{resumen.aprobaciones}</b> {resumen.aprobaciones === 1 ? 'aprobación' : 'aprobaciones'}
        </p>
        <p className={estilos.resumenMal}>
          <b>{resumen.rechazos}</b> {resumen.rechazos === 1 ? 'rechazo' : 'rechazos'}
        </p>
      </div>

      {/* ── Lo que pasó ────────────────────────────────────────────────── */}
      {error ? (
        <Aviso tipo="error">{(error as Error).message}</Aviso>
      ) : isLoading ? (
        <Cargando mensaje="CONSULTANDO BITÁCORA CENTRAL..." />
      ) : visibles.length === 0 ? (
        <p className={estilos.vacio}>
          {registros.length === 0
            ? 'Nadie hizo nada en este periodo.'
            : 'Ninguna acción con esos filtros.'}
        </p>
      ) : vista === 'tiempo' ? (
        <LineaDeTiempo registros={visibles} />
      ) : (
        <PorPersona registros={visibles} />
      )}
    </div>
  )
}
